import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Group, Select, Stack, Text, TextInput, Tooltip } from "@mantine/core";
import { Alert, Badge, Button, ConfirmModal, EmptyState, PageHeader, SectionCard } from "@xplatform/design-system";
import { AdminTable, col } from "@components/AdminTable";
import {
    IconFolder,
    IconSettings,
    IconPlus,
    IconEdit,
    IconTrash,
    IconSearch,
    IconArrowsSplit,
    IconBrandOnedrive,
} from "@tabler/icons-react";
import { useDoctype } from "@hooks/flow";
import { useSelectedOrganisation } from "xplatform_shell/useSelectedOrganisation";
import { translateFlowType } from "@utils/flowTypeTranslations";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import { useTranslation } from "@hooks/useTranslation";
import { CreateDoctypeDialog, EditDoctypeDialog } from "@src/features";
import type { DocumentTypeDetailViewModel } from "@models/doctype";
import { getLastPartOfUid } from "@src/utils/uidUtil";

export const DoctypeManagementPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { canEditDoctype, canCreateDoctype, isSuperAdmin } = useAdminPermissions();
    const {
        doctypesDetail,
        isLoading,
        error,
        fetchDoctypesDetailByProject,
        deleteDoctype,
    } = useDoctype();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingDoctype, setEditingDoctype] =
        useState<DocumentTypeDetailViewModel | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deletingDoctype, setDeletingDoctype] =
        useState<DocumentTypeDetailViewModel | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const { organisation } = useSelectedOrganisation();

    const categories = useMemo(
        () => [...new Set(doctypesDetail.map((d) => d.categoryName).filter((name): name is string => !!name))].sort(),
        [doctypesDetail],
    );
    const query = search.trim().toLowerCase();
    const visible = doctypesDetail.filter((d) =>
        (!query || [d.docTypeName, d.docTypeCode, d.description].some((v) => v?.toLowerCase().includes(query)))
        && (!categoryFilter || d.categoryName === categoryFilter)
        && (!statusFilter || d.status.toLowerCase() === statusFilter));

    /** The list API returns organisation ids only: name the caller's own, and the shared (no-org) ones. */
    const organisationLabel = (id: string | null) => {
        if (!id) return t("admin.documentTypes.labels.shared");
        if (id === organisation?.organisationId) return organisation.organisationName;
        return getLastPartOfUid(id);
    };

    useEffect(() => {
        fetchDoctypesDetailByProject();
    }, [fetchDoctypesDetailByProject]);

    const handleViewFlows = (docTypeId: string, docTypeCode: string) => {
        navigate(`/admin/doctypes/${docTypeId}/flows`, {
            state: { docTypeCode },
        });
    };

    const handleOpenDialog = () => {
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleDoctypeCreated = async () => {
        await fetchDoctypesDetailByProject();
    };

    const handleEditDoctype = (doctype: DocumentTypeDetailViewModel) => {
        setEditingDoctype(doctype);
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingDoctype(null);
    };

    const handleDoctypeUpdated = async () => {
        await fetchDoctypesDetailByProject();
    };

    const handleDeleteClick = (doctype: DocumentTypeDetailViewModel) => {
        setDeletingDoctype(doctype);
        setDeleteError(null);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setDeletingDoctype(null);
        setDeleteError(null);
    };

    const handleDeleteConfirm = async () => {
        if (!deletingDoctype) return;
        try {
            await deleteDoctype(deletingDoctype.id);
            setDeleteConfirmOpen(false);
            setDeletingDoctype(null);
            setDeleteError(null);
        } catch (err) {
            setDeleteError(
                err instanceof Error ? err.message : "Failed to delete document type",
            );
        }
    };

    const columns = [
        col.twoLine<DocumentTypeDetailViewModel>("name", t("admin.table.columns.name"), (d) => ({
            primary: d.docTypeName, secondary: d.docTypeCode,
        }), { width: "16%", mono: true }),
        col.text<DocumentTypeDetailViewModel>("description", t("admin.table.columns.description"), (d) => d.description,
                                              { dimmed: true }),
        col.text<DocumentTypeDetailViewModel>("category", t("admin.categories.title"), (d) => d.categoryName, { width: "11%" }),
        col.badge<DocumentTypeDetailViewModel>("flow", t("admin.documentTypes.columns.liveFlow"), (d) => {
            const live = d.activeFlow;
            if (live === undefined) return <Text size="sm" c="dimmed">-</Text>;
            return live ? (
                <Text size="sm" truncate title={translateFlowType(live.flowType, t)}>
                    {t("admin.flowConfigs.versions.label", { version: live.version })}
                    <Text span size="xs" c="dimmed"> · {translateFlowType(live.flowType, t)}</Text>
                </Text>
            ) : (
                <Text size="sm" c="orange.8">{t("admin.documentTypes.labels.noLiveFlow")}</Text>
            );
        }, 170),
        col.badge<DocumentTypeDetailViewModel>("integrations", t("admin.documentTypes.columns.integrations"), (d) => {
            const mapping = d.mappingConfig?.enabled;
            if (!mapping && !d.sharepointEnabled) return <Text size="sm" c="dimmed">-</Text>;
            return (
                <Group gap="xs" wrap="nowrap">
                    {mapping && (
                        <Tooltip label={t("admin.documentTypes.labels.mappingTo", {
                            service: d.mappingConfig?.service, plugin: d.mappingConfig?.pluginId,
                        })}>
                            <IconArrowsSplit size={18} aria-label={t("admin.documentTypes.sections.mapping")} />
                        </Tooltip>
                    )}
                    {d.sharepointEnabled && (
                        <Tooltip label={t("admin.documentTypes.sections.sharepoint")}>
                            <IconBrandOnedrive size={18} aria-label={t("admin.documentTypes.sections.sharepoint")} />
                        </Tooltip>
                    )}
                </Group>
            );
        }, 136),
        col.text<DocumentTypeDetailViewModel>("organization", t("admin.table.columns.organization"),
                                              (d) => organisationLabel(d.organizationId), { width: "12%", dimmed: true }),
        col.badge<DocumentTypeDetailViewModel>("status", t("admin.table.columns.status"), (d) => (
            <Badge size="sm" color={d.status.toLowerCase() === "active" ? "green" : "gray"}>
                {t(`admin.table.status.${d.status.toLowerCase()}`)}
            </Badge>
        )),
        col.date<DocumentTypeDetailViewModel>("updated", t("admin.table.columns.updated"), (d) => d.updatedAt),
        ...(canEditDoctype
            ? [col.actions<DocumentTypeDetailViewModel>(t("admin.table.columns.actions"), (d) => [
                { label: t("common.edit"), icon: <IconEdit size={16} />, onClick: () => handleEditDoctype(d) },
                { label: t("admin.table.tooltips.viewFlowConfigs"), icon: <IconSettings size={16} />,
                  onClick: () => handleViewFlows(d.id, d.docTypeCode) },
                { label: t("common.delete"), icon: <IconTrash size={16} />, color: "red", hidden: !isSuperAdmin,
                  onClick: () => handleDeleteClick(d) },
            ])]
            : []),
    ];

    return (
        <Stack gap="md">
            <PageHeader
                title={t("admin.documentTypes.title")}
                icon={<IconFolder size={28} />}
                actions={canCreateDoctype ? (
                    <Button leftSection={<IconPlus size={16} />} onClick={handleOpenDialog}>
                        {t("admin.documentTypes.actions.createDocType")}
                    </Button>
                ) : undefined}
            />

            {error && <Alert color="red">{error}</Alert>}

            <SectionCard>
                <Group gap="sm" mb="md" wrap="wrap">
                    <TextInput
                        leftSection={<IconSearch size={16} />}
                        placeholder={t("admin.documentTypes.filters.search")}
                        aria-label={t("admin.documentTypes.filters.search")}
                        value={search}
                        onChange={(event) => setSearch(event.currentTarget.value)}
                        w={280}
                    />
                    <Select
                        placeholder={t("admin.documentTypes.filters.allCategories")}
                        aria-label={t("admin.categories.title")}
                        data={categories}
                        value={categoryFilter}
                        onChange={setCategoryFilter}
                        clearable
                        searchable
                        w={200}
                        comboboxProps={{ withinPortal: false }}
                    />
                    <Select
                        placeholder={t("admin.documentTypes.filters.allStatuses")}
                        aria-label={t("admin.table.columns.status")}
                        data={[
                            { value: "active", label: t("admin.table.status.active") },
                            { value: "inactive", label: t("admin.table.status.inactive") },
                        ]}
                        value={statusFilter}
                        onChange={setStatusFilter}
                        clearable
                        w={160}
                        comboboxProps={{ withinPortal: false }}
                    />
                    {visible.length !== doctypesDetail.length && (
                        <Text size="sm" c="dimmed">
                            {t("admin.documentTypes.filters.showing", { count: visible.length, total: doctypesDetail.length })}
                        </Text>
                    )}
                </Group>
                <AdminTable
                    columns={columns}
                    data={visible}
                    getRowKey={(d) => d.id}
                    onRowClick={canEditDoctype ? (d) => handleViewFlows(d.id, d.docTypeCode) : undefined}
                    loading={isLoading}
                    emptyState={
                        <EmptyState
                            icon={<IconSettings size={32} />}
                            title={t("admin.documentTypes.empty.noDocTypes")}
                            description={t("admin.documentTypes.empty.description")}
                        />
                    }
                />
            </SectionCard>

            <CreateDoctypeDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                onSuccess={handleDoctypeCreated}
            />

            {editingDoctype && (
                <EditDoctypeDialog
                    open={editDialogOpen}
                    onClose={handleCloseEditDialog}
                    onSuccess={handleDoctypeUpdated}
                    doctype={editingDoctype}
                />
            )}

            <ConfirmModal
                opened={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title={`${t("common.delete")} ${t("admin.documentTypes.title")}`}
                confirmLabel={t("common.delete")}
                cancelLabel={t("common.cancel")}
                danger
                loading={isLoading}
                portalProps={{ target: "#admin-portal-root" }}
                message={
                    <>
                        {deleteError && (
                            <Alert color="red" mb="sm">
                                {deleteError}
                            </Alert>
                        )}
                        <Text size="sm">
                            Are you sure you want to delete{" "}
                            <strong>{deletingDoctype?.docTypeName}</strong> (
                            {deletingDoctype?.docTypeCode})? This will also delete all
                            associated flow configurations. This action cannot be undone.
                        </Text>
                    </>
                }
            />
        </Stack>
    );
};
